import { formatToDollar, formatToPercent } from "@inrev/common";
import { SuretyBondPreview } from "@portal/api/bond/agent";
import type { DynamicDataTableColumn } from "@portal/components/ui/DynamicDataTable/types";
import { RowActionButtons } from "@portal/components/ui/row-action-buttons/RowActionButtons";
import {
	bondStatusDisplayLabelMap,
	getStatusTagByBondStatus,
} from "@portal/components/ui/StatusTag";
import { DateTime } from "luxon";
import { bondTypeLabelMap } from "../../../constants";
import { formatDateForCsv, formatEmpty } from "../../../utils";
import { AdminAssigneeControls } from "../shared/AdminAssigneeControls";

type AdminBondPreview = SuretyBondPreview & { underwriterName: string };

export const getBondTableColumns = (
	isMinimalUIEnabled: boolean,
): readonly DynamicDataTableColumn<AdminBondPreview>[] => [
	{
		id: "underwriter",
		accessorKey: "underwriterName",
		headerFilter: { type: "checkbox" },
		cellStyle: { noTruncate: true },
		csv: { rowObjectKey: "underwriterName", fileColumnHeader: "Underwriter" },
		size: 60,
		enableSorting: true,
		cell: ({ row }) =>
			row.original.underwriterUserId ? (
				<RowActionButtons alwaysShow={true}>
					<AdminAssigneeControls
						assignedEntity={{ entityType: "bond", entityId: row.original.id }}
						currentAssignee={row.original.underwriterUserId}
					/>
				</RowActionButtons>
			) : null,
	},
	{
		id: "status",
		accessorFn: (row) => bondStatusDisplayLabelMap[row.status] ?? row.status,
		header: "Status",
		size: 120,
		minSize: 120,
		enableSorting: true,
		headerFilter: { type: "checkbox" },
		cell: ({ row }) => getStatusTagByBondStatus(row.original.status),
	},
	{
		id: "type",
		header: "Type",
		accessorFn: ({ type }) => bondTypeLabelMap[type],
		size: 77,
		minSize: 77,
		enableSorting: true,
		headerFilter: { type: "checkbox" },
	},
	{
		id: "bondNumber",
		accessorKey: "number",
		header: "Bond Number",
		size: 180,
		minSize: 180,
		enableSorting: true,
	},
	{
		id: "accountName",
		accessorFn: ({ accountName }) => formatEmpty(accountName),
		header: "Account",
		size: 180,
		minSize: 180,
		enableSorting: true,
		headerFilter: { type: "checkbox" },
	},
	{
		id: "amount",
		accessorKey: "amount",
		header: "Amount",
		size: 130,
		minSize: 130,
		enableSorting: true,
		cell: ({ row }) => formatEmpty(formatToDollar(row.original.amount)),
	},
	{
		id: "rate",
		header: "Rate",
		accessorFn: ({ rate }) => rate ?? 0,
		size: 130,
		minSize: 130,
		enableSorting: true,
		cell: ({ row }) => formatToPercent(row.original.rate),
	},
	{
		id: "premium",
		accessorKey: "premium",
		header: "Premium",
		size: 130,
		minSize: 130,
		enableSorting: true,
		cell: ({ row }) => formatToDollar(row.original.premium, true),
	},
	{
		id: "principalName",
		accessorFn: ({ principalName }) => formatEmpty(principalName),
		header: "Principal",
		size: 180,
		minSize: 130,
		enableSorting: true,
		headerFilter: { type: "checkbox" },
	},
	{
		id: "obligeeName",
		accessorKey: "obligeeName",
		header: "Obligee",
		size: 200,
		minSize: 150,
		enableResizing: true,
		enableSorting: true,
	},
	{
		id: "description",
		accessorFn: ({ description, name }) => formatEmpty(isMinimalUIEnabled ? name : description),
		header: isMinimalUIEnabled ? "Project Name" : "Description",
		size: 200,
		minSize: 150,
		enableResizing: true,
		enableSorting: true,
	},
	{
		id: "effectiveDate",
		accessorFn: ({ effectiveDate }) => DateTime.fromISO(effectiveDate).toJSDate(),
		header: "Effective Date",
		size: 150,
		minSize: 130,
		enableSorting: true,
		csv: { formatValue: formatDateForCsv },
		cell: ({ row }) =>
			DateTime.fromISO(row.original.effectiveDate).toLocaleString(DateTime.DATE_MED),
	},
	{
		id: "expectedCompletionDate",
		accessorFn: ({ expectedCompletionDate }) =>
			expectedCompletionDate ? DateTime.fromISO(expectedCompletionDate).toJSDate() : undefined,
		header: "Est. Completion Date",
		size: 200,
		minSize: 130,
		enableSorting: true,
		sortingFn: "datetime",
		csv: { formatValue: formatDateForCsv },
		cell: ({ row }) =>
			formatEmpty(
				row.original.expectedCompletionDate
					? DateTime.fromISO(row.original.expectedCompletionDate).toLocaleString(DateTime.DATE_MED)
					: row.original.expectedCompletionDate,
			),
	},
	{
		id: "entityTags",
		header: "Tags",
		accessorFn: ({ entityTags }) => entityTags?.map((tag) => tag.displayName) ?? [],
		enableResizing: true,
		enableSorting: true,
		enableHiding: true,
		headerFilter: { type: "checkbox-array" },
		cell: ({ row }) => row.original.entityTags?.map((tag) => tag.displayName).join(", ") || "--",
	},
];
